// Library Methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import ExamCardContainer from "../card-ui/ExamCardContainer/ExamCardContainer";
import ExamCardHeader from "../card-ui/ExamCardHeader/ExamCardHeader";
import ExamCardButton from "../card-ui/ExamCardButton/ExamCardButton";
import TitleWithTooltip from "src/components/shared/text/TitleWithTooltip/TitleWithTooltip";
import ExamCardToggles from "../card-ui/ExamCardToggles/ExamCardToggles";

// Types
import { SelectedExamCardType } from "src/services/exams/types";

// Constants
import { DEFAULT_MOTILITY_CARD } from "src/constants/enums";

type OcularMotilityCardType = {
  label: string;
  selectedExamCard: SelectedExamCardType | undefined;
  updateExamCardProperty: (
    propertyName: keyof SelectedExamCardType,
    value: SelectedExamCardType[keyof SelectedExamCardType]
  ) => void;
  onRemoveCard: () => void;
};

const OcularMotilityCard = ({
  label,
  selectedExamCard,
  updateExamCardProperty,
  onRemoveCard,
}: OcularMotilityCardType) => {
  const { t } = useTranslation();

  const examCardInfo = selectedExamCard ?? DEFAULT_MOTILITY_CARD;

  const { examType, optionalToggles } = examCardInfo;

  const ocularMotilityMethods = useMemo(
    () => [
      { type: "H_PATTERN", label: t("H-Pattern") },
      { type: "NINE_GAZE", label: t("9 Gaze") },
    ],
    [t]
  );

  const textStyle = {
    color: "#FFF",
    fontSize: "12px",
  };

  // A button only needs to wrap to a new line once its own label can't fit
  // -- not at some arbitrary shared width. "ch" scales with character
  // count, so each button's floor tracks its own text; +2ch covers the
  // button's horizontal padding/border.
  const buttonMinWidth = (buttonLabel: string) => `${buttonLabel.length + 2}ch`;

  return (
    <ExamCardContainer>
      <ExamCardHeader isNew label={label} onRemoveCard={onRemoveCard} />
      <Grid
        item
        container
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        gap="8px"
        flexGrow={1}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "8px",
          }}
        >
          {/* Method */}
          <Grid item display="flex" flexDirection="column" gap="4px">
            <TitleWithTooltip
              title={t("Method")}
              tooltip={t("Selects the test method.")}
              titleStyle={textStyle}
              tooltipStyle={textStyle}
            />

            <Grid container item display="flex" flexWrap="wrap" gap="4px">
              {ocularMotilityMethods.map((omMethod) => (
                <Grid
                  item
                  xs
                  fontSize="12px"
                  minWidth={buttonMinWidth(omMethod.label)}
                  key={omMethod.type}
                >
                  <ExamCardButton
                    isSelected={omMethod.type === examType}
                    updateValue={() =>
                      updateExamCardProperty("examType", omMethod.type)
                    }
                    label={omMethod.label}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          <Divider color="#5B6D7A" />

          <ExamCardToggles
            optionalToggles={optionalToggles}
            updateExamCardProperty={updateExamCardProperty}
            showToggles={DEFAULT_MOTILITY_CARD.optionalToggles}
            disableToggles={{ requiresTutorial: true }}
          />
        </Box>
      </Grid>
    </ExamCardContainer>
  );
};

export default OcularMotilityCard;
